import { useLocationHash } from "@/hooks/useLocationHash";
import { type ReactNode, type MouseEvent as ReactMouseEvent, Suspense, useRef, useState, useEffect, useMemo } from "react";
import { SelfHealingSuspense } from "./self-healing-suspense";
import { Link, useLocation } from "wouter";
import { toast } from "sonner";
import { AddContactDialog } from "./add-contact-dialog";
import { useSession, MobyChatProvider, useMobyRepo, useMobyNodeClient, FileTransferProvider, FallbackFileTransferProvider, useFileTransfer } from "./provider";
import { mui } from "@/mui";
import {
  LogOut,
  LayoutGrid,
  MessageSquareIcon,
  Database,
  UserIcon,
  PhoneCall,
  PhoneOutgoing,
  PhoneIncoming,
  Mails,
  Monitor,
  Smartphone,
  ScanLine,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { LayoutContext, useLayoutPreferences, useLayoutPreferencesProvider, type LayoutMode, type FontSize } from "@/hooks/useLayoutPreferences";
import { FileTransferDrawer } from "./file/file-transfer-drawer";
import { ContactsDrawer } from "./contacts-drawer";
import { EmailDrawer } from "./email-drawer";
import { StreamConnectionDrawer } from "./stream-connection-drawer";
import { ChatDrawer } from "./chat-drawer";
import { CalendarDrawer } from "./calendar-drawer";
import { MobyNetLogo } from "./moby-net-logo";
import { useNearbyEvents } from "@/hooks/useCalendarEvents";
import { useMediaSessions } from "@/hooks/useMediaSessions";
import { PersistentAudioLayer } from "./persistent-audio-layer";

export function MobyChatPageLayout({ children }: { children: ReactNode }) {
  const session = useSession();
  const hash = useLocationHash();
  const layoutCtx = useLayoutPreferencesProvider();
  const [, navigate] = useLocation();

  // HOOKS FIX: useRef must be called unconditionally (React rules of hooks).
  // Placed ABOVE the early return to avoid React error #300 during logout.
  // CRITICAL: Use session.address as database ID, NOT session.name
  // session.name can change via user preferences, which would create a new database
  // and lose all conversations/contacts. session.address is cryptographically stable.
  const sessionIdRef = useRef(session?.address ?? '');
  const sessionSecretRef = useRef(session?.secret ?? '');
  const sessionNameRef = useRef(session?.name ?? '');

  // SAFE REDIRECT: useEffect fires AFTER paint, not during React's commit phase.
  // The old <Redirect> used useLayoutEffect (synchronous, during commit).
  // During the onboarding→chats SPA transition, the useSuspenseLiveQuery cache
  // for session may still hold `undefined` (liveQuery microtask hasn't fired).
  // The Redirect would fire synchronously → navigate to login page → login page's
  // Redirect fires back to /chats → synchronous useLayoutEffect loop → error #185.
  // useEffect breaks the loop: by the time it fires, liveQuery microtasks have
  // updated the session cache, so the redirect condition is false.
  useEffect(() => {
    if (session === undefined) {
      // 50ms delay: during the onboarding→chats SPA transition, the liveQuery cache
      // may hold `undefined` for session on first mount (transaction committed but
      // liveQuery microtask hasn't fired yet). The 50ms gives liveQuery time to
      // deliver the session update. When it does, React re-runs this effect with
      // session !== undefined — the cleanup clearTimeout() cancels the pending
      // redirect, and the new effect body skips the if-block. Net result: no
      // redirect fires during normal transitions. Only genuinely logged-out users
      // (session stays undefined after 50ms) get redirected to the login page.
      const t = setTimeout(() => navigate(`/${hash}`, { replace: true }), 50);
      return () => clearTimeout(t);
    }
  }, [session, hash, navigate]);

  if (session === undefined) {
    return null;
  }

  // CRITICAL FIX: useSuspenseLiveQuery returns new object references on every database change
  // This causes MobyChatProvider to unmount/remount repeatedly, breaking file transfers
  // Only update refs if the actual string values changed (not just object reference)
  if (sessionIdRef.current !== session.address) {
    sessionIdRef.current = session.address;
  }
  if (sessionSecretRef.current !== session.secret) {
    sessionSecretRef.current = session.secret;
  }
  if (sessionNameRef.current !== session.name) {
    sessionNameRef.current = session.name;
  }

  return (
    <LayoutContext.Provider value={layoutCtx}>
    <MobyChatProvider 
      id={sessionIdRef.current} 
      privateKey={sessionSecretRef.current}
      sessionName={sessionNameRef.current}
    >
      <FileTransferManagerWrapper>
        {/* Persistent Audio Layer - manages audio playback independent of tab navigation */}
        {/* Lives at root level so audio continues when user navigates between tabs */}
        <PersistentAudioLayer />
        
        <div
          className={`bg-surface-container text-on-surface flex h-dvh max-h-dvh flex-col`}
        >
          <Header />

          <div className="relative flex flex-1 overflow-hidden">
            <SelfHealingSuspense
              name="nav-rail"
              watchdogMs={3000}
              maxRemounts={2}
              fallback={
                <div className="bg-surface-container text-on-surface z-10 flex shrink-0 flex-col items-center px-0 md:px-1 pt-1 pb-2">
                  <div className="flex flex-col items-center gap-3 mt-16">
                    <div className="animate-pulse bg-surface-container-highest/30 rounded-full w-10 h-10" />
                    <div className="animate-pulse bg-surface-container-highest/20 rounded-full w-10 h-10" />
                    <div className="animate-pulse bg-surface-container-highest/20 rounded-full w-10 h-10" />
                  </div>
                </div>
              }
            >
              <NavigationRail />
            </SelfHealingSuspense>
            <div className={`flex flex-1 min-w-0 overflow-hidden ${layoutCtx.isCompact ? 'flex-col' : ''}`}>
              {children}
            </div>
          </div>
        </div>
        <AddContactDialog />
      </FileTransferManagerWrapper>
    </MobyChatProvider>
    </LayoutContext.Provider>
  );
}

function Header() {
  const { isCompact } = useLayoutPreferences();

  return (
    <div className="bg-surface-container text-on-surface relative z-10 flex items-center justify-between p-2 md:p-4">
      <Link to="/" className="flex items-center gap-1 md:gap-2 shrink-0">
        <MobyNetLogo className="h-7 md:h-11" />
        <h1 className="text-headline-sm md:text-headline-md">MobyChat™</h1>
      </Link>

      {/* Center: date + upcoming-event cycler — calendar icon/date opens calendar */}
      <Suspense fallback={<HeaderDatePillFallback />}>
        <HeaderDatePill isCompact={isCompact} />
      </Suspense>

      {/* Spacer to balance the justify-between */}
      <div className="shrink-0 w-0 md:w-11" />
    </div>
  );
}

/** Format a timestamp (or "now") as "Tue, May 5" (no time — time was a CPU-stall tell). */
function formatDate(ts: number = Date.now()): string {
  return new Date(ts).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** True when two timestamps fall on the same calendar day in the local timezone. */
function isSameLocalDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Navigate to the full calendar panel (same destination as the nav-rail "Open Calendar" button). */
function openCalendarPanel() {
  window.dispatchEvent(new CustomEvent('calendar-panel-open'));
}

/** Open the calendar panel AND the day-dialog for a specific event's date. */
function openCalendarPanelToEvent(startTime: number) {
  openCalendarPanel();
  // Defer the day-dialog dispatch so the panel mounts and its 'calendar-day-open'
  // listener is registered before we fire the event.
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent('calendar-day-open', { detail: { date: startTime } }),
    );
  }, 0);
}

function HeaderDatePillFallback() {
  return (
    <button
      type="button"
      onClick={openCalendarPanel}
      title="Open calendar"
      className="flex items-center gap-1.5 text-title-md md:text-title-lg text-on-surface-variant select-none hover:text-on-surface transition cursor-pointer bg-transparent border-none"
    >
      <CalendarDays size={20} />
      <span>{formatDate()}</span>
    </button>
  );
}

/**
 * Date + cycling event pill.
 *
 * Click targets:
 *   - Calendar icon / date / "No event scheduled" placeholder / chevrons → open calendar panel
 *   - Actual event title+time → open calendar panel + the day dialog for that event's date
 *
 * Chevrons cycle through a window of nearby events (past + upcoming). Default
 * cursor position is the next upcoming event (or the most recent past event if
 * there are none upcoming).
 */
function HeaderDatePill({ isCompact }: { isCompact: boolean }) {
  const [todayStr, setTodayStr] = useState(() => formatDate());
  const { combined, upcomingStartIndex } = useNearbyEvents(10, 10);

  // Default cursor: first upcoming event (or last past event as fallback).
  const defaultIndex =
    combined.length === 0
      ? 0
      : upcomingStartIndex < combined.length
        ? upcomingStartIndex
        : combined.length - 1;

  const [eventIndex, setEventIndex] = useState(defaultIndex);
  const [userMovedCursor, setUserMovedCursor] = useState(false);

  // Keep cursor pinned to the "next upcoming" default until the user explicitly
  // cycles. This way new events sliding into the upcoming window move the
  // default cursor forward naturally.
  useEffect(() => {
    if (!userMovedCursor) {
      setEventIndex(defaultIndex);
    } else if (eventIndex >= combined.length) {
      setEventIndex(Math.max(0, combined.length - 1));
    }
  }, [defaultIndex, combined.length, userMovedCursor, eventIndex]);

  // Re-check the date once a minute; only re-renders on day rollover.
  useEffect(() => {
    const id = setInterval(() => {
      setTodayStr(prev => {
        const next = formatDate();
        return next === prev ? prev : next;
      });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const hasEvents = combined.length > 0;
  const canCycle = combined.length > 1;
  const current = hasEvents ? combined[Math.min(eventIndex, combined.length - 1)] : null;

  // Date field follows the cursor: shows the current event's date when one is
  // selected, otherwise falls back to today.
  const displayDateStr = current ? formatDate(current.startTime) : todayStr;

  const cyclePrev = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (!canCycle) {
      openCalendarPanel();
      return;
    }
    setUserMovedCursor(true);
    setEventIndex(i => (i - 1 + combined.length) % combined.length);
  };
  const cycleNext = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (!canCycle) {
      openCalendarPanel();
      return;
    }
    setUserMovedCursor(true);
    setEventIndex(i => (i + 1) % combined.length);
  };

  const timeStr = current
    ? new Date(current.startTime).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  // Status pill: Past / Today / Next / Upcoming. The "Next" label is reserved
  // for the very next future event (the default cursor target); subsequent
  // future events are "Upcoming". Past events are "Past". Future events landing
  // on today's calendar day get "Today".
  let statusLabel: 'Past' | 'Today' | 'Next' | 'Upcoming' | null = null;
  if (current) {
    const now = Date.now();
    if (current.startTime < now) {
      statusLabel = 'Past';
    } else if (isSameLocalDay(current.startTime, now)) {
      statusLabel = 'Today';
    } else if (combined.indexOf(current) === upcomingStartIndex) {
      statusLabel = 'Next';
    } else {
      statusLabel = 'Upcoming';
    }
  }
  const isPast = statusLabel === 'Past';
  const statusPillClass: Record<NonNullable<typeof statusLabel>, string> = {
    Past: 'bg-on-surface-variant/10 text-on-surface-variant/70',
    Today: 'bg-tertiary-container text-on-tertiary-container',
    Next: 'bg-primary-container text-on-primary-container',
    Upcoming: 'bg-secondary-container text-on-secondary-container',
  };

  const arrowBase =
    'flex items-center justify-center rounded-full p-1 bg-transparent border-none transition cursor-pointer text-on-surface-variant hover:text-on-surface hover:bg-on-surface/8';

  return (
    <div className="flex items-center gap-1 text-title-md md:text-title-lg text-on-surface-variant select-none">
      <button
        type="button"
        onClick={cyclePrev}
        aria-label={canCycle ? 'Previous event' : 'Open calendar'}
        title={canCycle ? 'Previous event' : 'Open calendar'}
        className={arrowBase}
      >
        <ChevronLeft size={20} />
      </button>

      <button
        type="button"
        onClick={openCalendarPanel}
        title="Open calendar"
        className="flex items-center gap-1.5 rounded-medium px-1.5 py-0.5 hover:text-on-surface hover:bg-on-surface/8 transition cursor-pointer bg-transparent border-none"
      >
        <CalendarDays size={20} />
        <span>{displayDateStr}</span>
      </button>

      {!isCompact && (
        <>
          <span className="text-outline">|</span>
          {current && statusLabel ? (
            <button
              type="button"
              onClick={() => openCalendarPanelToEvent(current.startTime)}
              title="Open event"
              className={`flex items-center gap-1.5 rounded-medium px-1.5 py-0.5 text-body-sm md:text-body-md hover:text-on-surface hover:bg-on-surface/8 transition cursor-pointer bg-transparent border-none ${
                isPast ? 'text-on-surface-variant/60' : ''
              }`}
            >
              <span
                className={`text-label-sm font-medium px-1.5 py-0.5 rounded-full ${statusPillClass[statusLabel]}`}
              >
                {statusLabel}
              </span>
              <span className="truncate max-w-[220px]">{current.title}</span>
              <span className="text-on-surface-variant/70">{timeStr}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={openCalendarPanel}
              title="Open calendar"
              className="rounded-medium px-1.5 py-0.5 text-body-sm md:text-body-md italic text-on-surface-variant/70 hover:text-on-surface hover:bg-on-surface/8 transition cursor-pointer bg-transparent border-none"
            >
              No event scheduled
            </button>
          )}
        </>
      )}

      <button
        type="button"
        onClick={cycleNext}
        aria-label={canCycle ? 'Next event' : 'Open calendar'}
        title={canCycle ? 'Next event' : 'Open calendar'}
        className={arrowBase}
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
}

function LayoutSettingsPopover() {
  const { mode, setMode, fontSize, setFontSize, isCompact } = useLayoutPreferences();
  const [open, setOpen] = useState(false);

  const modeOptions: { value: LayoutMode; label: string; icon: ReactNode }[] = [
    { value: 'auto', label: 'Auto', icon: <ScanLine size={16} /> },
    { value: 'desktop', label: 'Desktop', icon: <Monitor size={16} /> },
    { value: 'compact', label: 'Compact', icon: <Smartphone size={16} /> },
  ];

  const fontOptions: { value: FontSize; label: string }[] = [
    { value: 'small', label: 'S' },
    { value: 'normal', label: 'M' },
    { value: 'large', label: 'L' },
  ];

  return (
    <div className="relative">
      <mui.FloatingActionButton
        icon={<LayoutGrid size={24} />}
        onClick={() => setOpen(prev => !prev)}
      />
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-full ml-2 top-0 z-50 bg-surface-container-high rounded-extra-large shadow-lg p-4 w-52">
            <h3 className="text-title-sm text-on-surface mb-3">Layout</h3>
            <div className="space-y-1 mb-4">
              {modeOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setMode(opt.value); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-small text-body-sm transition ${
                    mode === opt.value
                      ? 'bg-secondary-container text-on-secondary-container'
                      : 'text-on-surface hover:bg-on-surface/8'
                  }`}
                >
                  {opt.icon}
                  {opt.label}
                  {opt.value === 'auto' && (
                    <span className="ml-auto text-label-sm text-on-surface-variant">
                      {isCompact ? '(compact)' : '(desktop)'}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <h3 className="text-title-sm text-on-surface mb-2">Font Size</h3>
            <div className="flex gap-2">
              {fontOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFontSize(opt.value)}
                  className={`flex-1 px-2 py-1.5 rounded-small text-body-md font-medium transition ${
                    fontSize === opt.value
                      ? 'bg-secondary-container text-on-secondary-container'
                      : 'text-on-surface hover:bg-on-surface/8'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NavigationRail() {
  const mediaSessions = useMediaSessions();
  const [activeDrawer, setActiveDrawer] = useState<string | null>(null);
  
  const toggleDrawer = (name: string) => {
    setActiveDrawer(prev => prev === name ? null : name);
  };

  // Listen for header click to open calendar drawer
  useEffect(() => {
    const handler = () => setActiveDrawer(prev => prev === 'calendar' ? null : 'calendar');
    window.addEventListener('open-calendar-drawer', handler);
    return () => window.removeEventListener('open-calendar-drawer', handler);
  }, []);
  
  // Deduplicate sessions by sessionId
  const uniqueSessions = Array.from(
    new Map(mediaSessions.map(s => [s.sessionId, s])).values()
  );
  
  // Check for incoming calls (remote initiated, not active)
  const hasIncomingCall = uniqueSessions.some(s => 
    !s.session.initiatedLocally && s.session.state !== 'active'
  );
  
  // Check for outgoing calls (local initiated, not active)
  const hasOutgoingCall = uniqueSessions.some(
    s => s.session.initiatedLocally && s.session.state !== 'active'
  );
  
  // Determine which icon to show
  const streamIcon = hasIncomingCall ? (
    <PhoneIncoming size={24} />
  ) : hasOutgoingCall ? (
    <PhoneOutgoing size={24} />
  ) : (
    <PhoneCall size={24} />
  );
  
  const repo = useMobyRepo();

  return (
    <div
      className={`bg-surface-container text-on-surface z-10 flex shrink-0 flex-col items-center justify-between px-0 md:px-1 pt-1 pb-2`}
    >
      <div className="flex flex-col items-center">
      <div className="my-4 md:my-10">
        <LayoutSettingsPopover />
      </div>
      <div className="grid items-center gap-3">
        <ContactsDrawer
          open={activeDrawer === 'contacts'}
          onOpenChange={(open) => setActiveDrawer(open ? 'contacts' : null)}
        >
          {({ unseenCount }) => (
            <mui.NavigationRail.Item
              icon={
                <div className="relative">
                  <UserIcon size={24} />
                  {unseenCount > 0 && (
                    <div className="absolute -top-1 -right-1 bg-secondary text-on-secondary text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unseenCount > 99 ? '99+' : unseenCount}
                    </div>
                  )}
                </div>
              }
              label="Contacts"
              active={activeDrawer === 'contacts'}
              onClick={() => toggleDrawer('contacts')}
            />
          )}
        </ContactsDrawer>
        <ChatDrawer
          open={activeDrawer === 'chat'}
          onOpenChange={(open) => setActiveDrawer(open ? 'chat' : null)}
        >
          {({ unseenCount }) => (
            <mui.NavigationRail.Item
              icon={
                <div className="relative">
                  <MessageSquareIcon fill="currentColor" size={24} />
                  {unseenCount > 0 && (
                    <div className="absolute -top-1 -right-1 bg-primary text-on-primary text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unseenCount > 99 ? '99+' : unseenCount}
                    </div>
                  )}
                </div>
              }
              label="Chat"
              active={activeDrawer === 'chat'}
              onClick={() => toggleDrawer('chat')}
            />
          )}
        </ChatDrawer>
        <FileTransferDrawer
          open={activeDrawer === 'files'}
          onOpenChange={(open) => setActiveDrawer(open ? 'files' : null)}
        >
          {({ unseenCount }) => (
            <mui.NavigationRail.Item
              icon={
                <div className="relative">
                  <Database size={24} />
                  {unseenCount > 0 && (
                    <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unseenCount > 99 ? '99+' : unseenCount}
                    </div>
                  )}
                </div>
              }
              label="Files"
              active={activeDrawer === 'files'}
              onClick={() => toggleDrawer('files')}
            />
          )}
        </FileTransferDrawer>
        <StreamConnectionDrawer
          open={activeDrawer === 'stream'}
          onOpenChange={(open) => setActiveDrawer(open ? 'stream' : null)}
        >
          <mui.NavigationRail.Item
            icon={
              <div className={(hasIncomingCall || hasOutgoingCall) ? "animate-call-ring p-1" : ""}>
                {streamIcon}
              </div>
            }
            label="Streams"
            active={activeDrawer === 'stream'}
            onClick={() => toggleDrawer('stream')}
          />
        </StreamConnectionDrawer>
        <CalendarDrawer
          open={activeDrawer === 'calendar'}
          onOpenChange={(open) => setActiveDrawer(open ? 'calendar' : null)}
        >
          {({ unseenCount }) => (
            <mui.NavigationRail.Item
              icon={
                <div className="relative">
                  <CalendarDays size={24} />
                  {unseenCount > 0 && (
                    <div className="absolute -top-1 -right-1 bg-tertiary text-on-tertiary text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unseenCount > 99 ? '99+' : unseenCount}
                    </div>
                  )}
                </div>
              }
              label="Calendar"
              active={activeDrawer === 'calendar'}
              onClick={() => toggleDrawer('calendar')}
            />
          )}
        </CalendarDrawer>
        <EmailDrawer
          open={activeDrawer === 'email'}
          onOpenChange={(open) => setActiveDrawer(open ? 'email' : null)}
        >
          <mui.NavigationRail.Item
            icon={<Mails size={24} />}
            label="Email"
            active={activeDrawer === 'email'}
            onClick={() => toggleDrawer('email')}
          />
        </EmailDrawer>
      </div>
      </div>

      {/* Log out — separated at bottom of rail */}
      <mui.NavigationRail.Item
        icon={<LogOut size={24} />}
        label="Log Out"
        onClick={async () => {
          await repo.invalidateSession();
          toast.success("Logged out.");
        }}
      />
    </div>
  );
}

/**
 * The FileTransferManagerWrapper component ensures that FileTransferProvider is available
 * for each connected peer, while maintaining backward compatibility with the FallbackFileTransferProvider
 * for the initial app state when no peers exist yet.
 * 
 * This component uses polling to check for peer connection changes and dynamically renders the appropriate providers.
 */
function FileTransferManagerWrapper({ children }: { children: ReactNode }) {
  const node = useMobyNodeClient();
  const [peers, setPeers] = useState<Map<string, any>>(new Map());
  
  // FIX: Use ref to track peers without causing effect to re-run
  const peersRef = useRef<Map<string, any>>(new Map());
  
  // Debug flag to control logging - set to false to disable most logs
  const DEBUG_LOGGING = false;
  
  // Use polling to check for peer connection changes
  useEffect(() => {
    // Initial state setup
    const initialPeers = new Map(node.peers);
    if (DEBUG_LOGGING) {
      console.log(`FileTransferManagerWrapper: Initial peer count: ${initialPeers.size}`);
      if (initialPeers.size > 0) {
        console.log('FileTransferManagerWrapper: Peers detected on mount:', 
          Array.from(initialPeers.entries()).map(([addr, peer]) => ({
            address: addr,
            connectionState: peer.connection?.connectionState || 'unknown',
            chatChannelOpen: peer.chat?.channel?.readyState === 'open' || false,
            fileChannelOpen: peer.file?.channel?.readyState === 'open' || false
          })));
      }
    }
    peersRef.current = initialPeers;
    setPeers(initialPeers);
    
    // Set up polling interval to check for peer changes
    const intervalId = setInterval(() => {
      // Only update state if there's an actual change in peers
      const currentPeers = node.peers;
      const prevPeers = peersRef.current;
      
      if (currentPeers.size !== prevPeers.size) {
        // Only log significant changes, like when we first get peers
        if (prevPeers.size === 0 && currentPeers.size > 0) {
          console.log(`FileTransferManagerWrapper: First peer(s) connected (${currentPeers.size})`);
        } else if (DEBUG_LOGGING) {
          console.log(`FileTransferManagerWrapper: Peer count changed: ${prevPeers.size} -> ${currentPeers.size}`);
        }
        const newPeers = new Map(currentPeers);
        peersRef.current = newPeers;
        setPeers(newPeers);
      } else {
        // Check if the peers are different even if count is the same
        let hasChanged = false;
        for (const [address] of currentPeers) {
          if (!prevPeers.has(address)) {
            hasChanged = true;
            break;
          }
        }
        if (hasChanged) {
          if (DEBUG_LOGGING) {
            console.log('FileTransferManagerWrapper: Peer list contents changed (same count)');
          }
          const newPeers = new Map(currentPeers);
          peersRef.current = newPeers;
          setPeers(newPeers);
        }
        
        // TRANSFER-AWARE: Also refresh if transfer activity changed on any peer.
        // primaryPeer selection prefers the peer with an active transfer, but that
        // selection only reruns when peers state updates. Without this, a transfer
        // starting on ATMob while W11 Edge is primaryPeer never switches the pointer —
        // adaptiveSync keeps polling the idle peer → empty maps → no Web Lock/silent audio
        // → browser throttles the sender tab → transfer collapses.
        if (!hasChanged) {
          const prevTransferPeer = Array.from(prevPeers.values())
            .find(p => (p.activeTransfers?.size ?? 0) > 0 || (p.sendQueue?.size ?? 0) > 0);
          const currTransferPeer = Array.from(currentPeers.values())
            .find(p => (p.activeTransfers?.size ?? 0) > 0 || (p.sendQueue?.size ?? 0) > 0);
          const transferPeerChanged = prevTransferPeer?.address !== currTransferPeer?.address;
          if (transferPeerChanged) {
            const newPeers = new Map(currentPeers);
            peersRef.current = newPeers;
            setPeers(newPeers);
          }
        }
        
        // Eliminate the periodic channel state logging - it's too verbose
        // Only log on very rare occasions (0.1% chance) if enabled
        if (DEBUG_LOGGING && currentPeers.size > 0 && Math.random() < 0.001) {
          console.log('FileTransferManagerWrapper: Current peer channel states:', 
            Array.from(currentPeers.entries()).map(([addr, peer]) => ({
              address: addr,
              connectionState: peer.connection?.connectionState || 'unknown',
              chatChannelOpen: peer.chat?.channel?.readyState === 'open' || false,
              fileChannelOpen: peer.file?.channel?.readyState === 'open' || false
            })));
        }
      }
    }, 1000); // Check every second
    
    // Cleanup polling on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [node]); // FIX: Removed 'peers' from dependencies to prevent infinite loop
  
  // Don't log on every peer count change - this was creating too many messages
  // Only log on significant changes if debugging
  useEffect(() => {
    if (DEBUG_LOGGING && peers.size > 0) {
      console.log(`FileTransferManagerWrapper: ${peers.size} peers available`);
    }
  }, [peers.size, DEBUG_LOGGING]);
  
  // UNMOUNT FIX v2: Always render a SINGLE stable FileTransferProvider wrapping children.
  // The previous fix (v1) still dynamically inserted FileTransferProvider wrappers via a
  // loop when peers appeared, changing the component tree structure between
  // FallbackFileTransferProvider and {children}. React treated this as a tree structure
  // change and unmounted/remounted all children — including EmailViewPanel during compose.
  //
  // Fix: Always render the same tree structure:
  //   FallbackFileTransferProvider → FileTransferProvider(peer|null) → {children}
  // When peers change, only the `peer` prop updates — no intermediate nodes are inserted
  // or removed, so React preserves all children without unmounting.
  //
  // TRANSFER-AWARE PRIMARY PEER FIX: Prefer the peer with an active transfer.
  // Previously picked the first connected peer in Map insertion order, which could be an
  // idle peer (e.g. W11 Edge or COS chrome) while ATMob is actively sending 33GB.
  // adaptiveSync polls peerRef.current.getActiveTransfers() — if that peer has no
  // transfers, hasActivity=false → consecutiveEmptyPolls hits 200+ → Web Lock and
  // silent audio are never held → browser throttles the sender tab → transfer collapses.
  //
  // Priority: (1) peer with large active transfer (>500MB), (2) peer with any active/
  // queued transfer, (3) any connected peer, (4) first peer (fallback).
  const peerList = peers.size > 0 ? Array.from(peers.values()) : [];
  const primaryPeer = peerList.length === 0 ? null
    : peerList.find(p => p.hasLargeActiveTransfer?.())
      || peerList.find(p =>
          (p.activeTransfers?.size ?? 0) > 0 ||
          (p.sendQueue?.size ?? 0) > 0 ||
          (p.receiveQueue?.size ?? 0) > 0
        )
      || peerList.find(p => p.connection?.connectionState === 'connected')
      || peerList[0];
  
  return (
    <FallbackFileTransferProvider>
      <FileTransferProvider peer={primaryPeer} node={node}>
        {children}
      </FileTransferProvider>
    </FallbackFileTransferProvider>
  );
}

